import { Currency, CurrencyAmount, Percent, Price, Token, V3_FACTORY_ADDRESSES } from '@kodiak-finance/sdk-core'
import {
  computePoolAddress,
  encodeSqrtRatioX96,
  nearestUsableTick,
  Pool,
  Position,
  priceToClosestTick,
  TICK_SPACINGS,
  TickMath,
} from '@kodiak-finance/v3-sdk'
import ISLAND_ABI from 'abis/island.json'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { BIG_INT_ZERO } from 'constants/misc'
import { MAX_ALLOWED_ZAP_PRICE_IMPACT, MAX_ZAP_USD_THRESHOLD, MIN_ZAP_USD_THRESHOLD } from 'constants/zap'
import { useCurrency, useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import useDebounce from 'hooks/useDebounce'
import { useIsland } from 'hooks/useIsland'
import { useIslandTickData } from 'hooks/useIslandTicks'
import { useIslandUnderlyingBalances } from 'hooks/useIslandUnderlyingBalances'
import { PoolState } from 'hooks/usePools'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { useV3Pool } from 'hooks/useV3Pools'
import useKXRouterZap from 'hooks/zapper/useKXRouterZap'
import JSBI from 'jsbi'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { ReactNode, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { SubgraphIsland } from 'state/kodiakIslands/types'
import { getTickToPrice } from 'utils/getTickToPrice'
import { parseUnits } from 'viem'
import { Address } from 'viem'
import { useReadContract } from 'wagmi'

import { useCurrencyBalances } from '../../connection/hooks'
import { AppState } from '../../index'
import { sanitizePriceImpact } from '../../routing/utils'
import {
  Bound,
  Field,
  resetSingleSidedValues,
  setSingleSided,
  setSingleSidedAmount,
  setSingleSidedCurrency,
  setStakingSlippageBPS,
  setZapSwapSlippage,
  switchFields,
  typeInput,
} from './actions'

export function useMintState(): AppState['mintIsland'] {
  return useAppSelector((state) => state.mintIsland)
}

export function useMintStateSingleSidedCurrency(): Currency | null | undefined {
  const { singleSidedCurrency } = useMintState()
  const currency = useCurrency(singleSidedCurrency)
  const nativeCurrency = useNativeCurrency()
  if (singleSidedCurrency === 'native') {
    return nativeCurrency
  }
  return currency
}

export function useMintActionHandlers(noLiquidity: boolean | undefined): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
  onSwitchField: (independentField: Field, typedValue: string, otherTypedValue: string) => void
  onSetSingleSided: (singleSided: boolean, singleSidedCurrency: string) => void
  onSetSingleAmount: (typedValue: string) => void
  onSetSingleSidedCurrency: (singleSidedCurrency: string) => void
  onSetZapSwapSlippage: (zapSwapSlippage: string) => void
  onSetSingleSidedStakingSlippageBPS: (stakingSlippageBPS: number) => void
  onResetSingleSidedValues: () => void
} {
  const dispatch = useAppDispatch()

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onSwitchField = useCallback(
    (independentField: Field, typedValue: string, otherTypedValue: string) => {
      dispatch(
        switchFields({
          independentField,
          typedValue,
          otherTypedValue,
        })
      )
    },
    [dispatch]
  )

  const onSetZapSwapSlippage = useCallback(
    (zapSwapSlippage: string) => {
      dispatch(setZapSwapSlippage({ zapSwapSlippage }))
    },
    [dispatch]
  )

  const onSetSingleSided = useCallback(
    (singleSided: boolean, singleSidedCurrency: string) => {
      dispatch(setSingleSided({ singleSided, singleSidedCurrency }))
    },
    [dispatch]
  )

  const onSetSingleAmount = useCallback(
    (typedValue: string) => {
      dispatch(setSingleSidedAmount({ singleSidedAmount: typedValue }))
    },
    [dispatch]
  )

  const onSetSingleSidedCurrency = useCallback(
    (singleSidedCurrency: string) => {
      dispatch(setSingleSidedCurrency({ singleSidedCurrency }))
    },
    [dispatch]
  )

  const onResetSingleSidedValues = useCallback(() => {
    dispatch(resetSingleSidedValues())
  }, [dispatch])

  const onSetSingleSidedStakingSlippageBPS = useCallback(
    (stakingSlippageBPS: number) => {
      dispatch(setStakingSlippageBPS({ stakingSlippageBPS }))
    },
    [dispatch]
  )

  return {
    onFieldAInput,
    onFieldBInput,
    onSwitchField,
    onSetSingleSided,
    onSetSingleAmount,
    onSetSingleSidedCurrency,
    onSetZapSwapSlippage,
    onResetSingleSidedValues,
    onSetSingleSidedStakingSlippageBPS,
  }
}

export function useDerivedMintInfo(
  islandAddress: Address | undefined,
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  baseCurrency: Currency | undefined
): {
  dependentField: Field
  currencies: { [field in Field]?: Currency }
  island: SubgraphIsland | null
  pool: Pool | null
  position?: Position
  poolState: PoolState
  ticks?: { [key: string]: number | undefined }
  pricesAtTicks: { [bound in Bound]?: Price<Token, Token> }
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  invalidPool: boolean
  invalidRange: boolean
  outOfRange: boolean
  depositADisabled: boolean
  depositBDisabled: boolean
  invertPrice: boolean
  ticksAtLimit: { [bound in Bound]?: boolean }
  price?: Price<Currency, Currency>
  noLiquidity?: boolean
  liquidityMinted?: CurrencyAmount<Token>
  poolTokenPercentage?: Percent
  error?: ReactNode
  amount0Max?: CurrencyAmount<Currency>
  amount1Max?: CurrencyAmount<Currency>
  amount0Min?: CurrencyAmount<Currency>
  amount1Min?: CurrencyAmount<Currency>
  amountSharesMin?: CurrencyAmount<Token>
  amountShares?: CurrencyAmount<Token>
  singleSided: boolean
  dependentAmountFetching: boolean
  // new
  singleSidedParsedAmount: string
  singleSidedStakingSlippageBPS: number
  singleSidedCurrencyUserBalance: CurrencyAmount<Currency> | undefined
} {
  const { account, chainId } = useConnection()
  const factoryAddress = V3_FACTORY_ADDRESSES[chainId]

  const { independentField, typedValue, singleSidedAmount, singleSided, stakingSlippageBPS } = useMintState()
  const singleSidedCurrencyToken = useMintStateSingleSidedCurrency()
  const debouncedSingleSidedAmount = useDebounce(
    singleSidedAmount ? parseUnits(singleSidedAmount, singleSidedCurrencyToken?.decimals ?? 18).toString() : '0',
    500
  )

  const dependentField = useMemo(() => {
    return independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A
  }, [independentField])

  // tokens
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA ?? undefined,
      [Field.CURRENCY_B]: currencyB ?? undefined,
    }),
    [currencyA, currencyB]
  )

  const { island, isLoading, isFetching, isUninitialized, isError } = useIsland(islandAddress)

  // Get the island token for creating CurrencyAmount objects
  const islandToken = useToken(islandAddress)

  // balances
  const userTokenBalances = useCurrencyBalances(
    account ?? undefined,
    useMemo(() => {
      const currencyASymbol = currencies[Field.CURRENCY_A]?.isNative
        ? currencies[Field.CURRENCY_A]?.symbol
        : currencies[Field.CURRENCY_A]?.symbol
      const currencyBSymbol = currencies[Field.CURRENCY_B]?.isNative
        ? currencies[Field.CURRENCY_B]?.symbol
        : currencies[Field.CURRENCY_B]?.symbol
      const singleSidedCurrencySymbol = singleSidedCurrencyToken?.isNative
        ? singleSidedCurrencyToken?.symbol
        : singleSidedCurrencyToken?.symbol
      if (
        singleSided &&
        singleSidedCurrencyToken &&
        currencyASymbol != singleSidedCurrencySymbol &&
        currencyBSymbol != singleSidedCurrencySymbol
      ) {
        return [currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B], singleSidedCurrencyToken]
      }
      return [currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B]]
    }, [currencies, singleSided, singleSidedCurrencyToken]),
    REFETCH_POLICY_FAST
  )

  const singleSidedCurrencyUserBalance = useMemo(() => {
    return userTokenBalances.find((balance) => balance?.currency.symbol === singleSidedCurrencyToken?.symbol)
  }, [userTokenBalances, singleSidedCurrencyToken])

  // formatted with tokens
  const [tokenA, tokenB, baseToken] = useMemo(
    () => [currencyA?.wrapped, currencyB?.wrapped, baseCurrency?.wrapped],
    [currencyA, currencyB, baseCurrency]
  )

  const [token0, token1] = useMemo(
    () =>
      tokenA && tokenB ? (tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]) : [undefined, undefined],
    [tokenA, tokenB]
  )

  const mintingBalances: { [field in Field]?: CurrencyAmount<Currency> } = useMemo(() => {
    if (currencyA === undefined || currencyB === undefined) {
      return {
        [Field.CURRENCY_A]: undefined,
        [Field.CURRENCY_B]: undefined,
      }
    }
    // when working with Bera
    return {
      [Field.CURRENCY_A]: userTokenBalances[0],
      [Field.CURRENCY_B]: userTokenBalances[1],
    }
  }, [userTokenBalances[0], userTokenBalances[1], currencyA, currencyB])

  const { ticks: ticksData, isLoading: tickLoading, isError: tickError } = useIslandTickData(islandAddress)

  const feeAmount = Number(island?.pool?.feeTier)

  const poolAddress = useMemo(() => {
    if (!factoryAddress || !tokenA || !tokenB || !feeAmount) {
      return null
    }

    return computePoolAddress({
      factoryAddress,
      tokenA: tokenB,
      tokenB: tokenA,
      fee: feeAmount,
    }).toLowerCase()
  }, [factoryAddress, tokenA, tokenB, feeAmount])

  // pool
  const [poolState, pool] = useV3Pool(poolAddress, chainId)

  const {
    underlyingAmount0,
    underlyingAmount1,
    totalSupply: islandTotalSupplyRaw,
    loading: underlyingBalancesFetching,
  } = useIslandUnderlyingBalances(islandAddress)

  const islandTotalSupply = useMemo(() => {
    if (!islandToken || !islandTotalSupplyRaw) return undefined
    return CurrencyAmount.fromRawAmount(islandToken, islandTotalSupplyRaw.toString())
  }, [islandToken, islandTotalSupplyRaw])

  const noLiquidity = poolState === PoolState.NOT_EXISTS

  const independentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    return tryParseCurrencyAmount(typedValue, currencies[independentField])
  }, [typedValue, currencies, independentField])

  const debouncedIndependentAmount = useDebounce(independentAmount, 500)

  const getMintAmountsInput: [string, string] | null = useMemo(() => {
    if (
      underlyingAmount0 === undefined ||
      underlyingAmount1 === undefined ||
      !JSBI.equal(JSBI.BigInt(underlyingAmount0.toString()), JSBI.BigInt(0)) ||
      !JSBI.equal(JSBI.BigInt(underlyingAmount1.toString()), JSBI.BigInt(0))
    ) {
      return null
    }

    if (!tokenA || !tokenB || !debouncedIndependentAmount) return null

    const uint119Max = '664613997892457936451903530140172287'

    const tokenAIsToken0 = tokenA.sortsBefore(tokenB)
    const userInputIsToken0 = tokenAIsToken0
      ? independentField === Field.CURRENCY_A
      : independentField === Field.CURRENCY_B

    if (userInputIsToken0) {
      return [debouncedIndependentAmount.quotient.toString(), uint119Max]
    } else {
      return [uint119Max, debouncedIndependentAmount.quotient.toString()]
    }
  }, [debouncedIndependentAmount, tokenA, tokenB, underlyingAmount0, underlyingAmount1, independentField])

  const {
    data: contractMintAmountsData,
    isLoading: contractLoading,
    isError: contractError,
  } = useReadContract({
    address: islandAddress,
    abi: ISLAND_ABI,
    functionName: 'getMintAmounts',
    args: getMintAmountsInput as [string, string],
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!getMintAmountsInput && !!islandAddress,
    },
  })

  const contractMintAmounts = contractMintAmountsData as [bigint, bigint, bigint] | undefined

  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (noLiquidity) {
      return undefined
    }

    if (!independentAmount || !tokenA || !tokenB) {
      return undefined
    }

    if (typedValue === '') return undefined

    if (
      underlyingAmount0 === undefined ||
      underlyingAmount0 === null ||
      underlyingAmount1 === undefined ||
      underlyingAmount1 === null
    ) {
      return undefined
    }

    const underlying0 = JSBI.BigInt(underlyingAmount0.toString())
    const underlying1 = JSBI.BigInt(underlyingAmount1.toString())

    if (JSBI.equal(underlying0, JSBI.BigInt(0)) && JSBI.equal(underlying1, JSBI.BigInt(0))) {
      if (contractMintAmounts) {
        const [amount0, amount1] = contractMintAmounts
        const dependentCurrency = currencies[dependentField]
        if (!dependentCurrency) return undefined

        // Determine token order directly
        const tokenAIsToken0 = tokenA.sortsBefore(tokenB)
        const dependentIsToken0 = tokenAIsToken0
          ? dependentField === Field.CURRENCY_A
          : dependentField === Field.CURRENCY_B

        if (dependentIsToken0) {
          return CurrencyAmount.fromRawAmount(dependentCurrency, amount0.toString())
        } else {
          return CurrencyAmount.fromRawAmount(dependentCurrency, amount1.toString())
        }
      }
      return undefined
    }

    if (JSBI.equal(underlying0, JSBI.BigInt(0)) || JSBI.equal(underlying1, JSBI.BigInt(0))) {
      if (pool) {
        const dependentCurrency = currencies[dependentField]
        if (!dependentCurrency) return undefined

        try {
          const independentToken = independentField === Field.CURRENCY_A ? tokenA : tokenB
          const poolPrice = pool.priceOf(independentToken)
          const wrappedIndependentAmount = CurrencyAmount.fromRawAmount(independentToken, independentAmount.quotient)
          const quotedAmount = poolPrice.quote(wrappedIndependentAmount)
          return CurrencyAmount.fromRawAmount(dependentCurrency, quotedAmount.quotient)
        } catch (error) {
          console.warn('Failed to calculate dependent amount for single-sided deposit:', error)
          return undefined
        }
      }
      return undefined
    }

    const dependentCurrency = currencies[dependentField]
    if (!dependentCurrency) return undefined

    const ratio =
      independentField === Field.CURRENCY_A
        ? JSBI.divide(JSBI.multiply(independentAmount.quotient, underlying1), underlying0)
        : JSBI.divide(JSBI.multiply(independentAmount.quotient, underlying0), underlying1)

    return CurrencyAmount.fromRawAmount(dependentCurrency, ratio)
  }, [
    noLiquidity,
    independentAmount,
    underlyingAmount0,
    underlyingAmount1,
    tokenA,
    tokenB,
    typedValue,
    currencies,
    dependentField,
    independentField,
    pool,
    contractMintAmounts,
  ])

  const mintAmounts = useMemo(() => {
    // For new islands, use contract result if available
    if (!islandTotalSupply || JSBI.equal(islandTotalSupply.quotient, JSBI.BigInt(0))) {
      if (contractMintAmounts) {
        const [amount0, amount1, mintAmount] = contractMintAmounts
        return {
          amount0: JSBI.BigInt(amount0.toString()),
          amount1: JSBI.BigInt(amount1.toString()),
          mintAmount: JSBI.BigInt(mintAmount.toString()),
        }
      }
      return { amount0: undefined, amount1: undefined, mintAmount: undefined }
    }

    if (!independentAmount || !dependentAmount || !underlyingAmount0 || !underlyingAmount1) {
      return { amount0: undefined, amount1: undefined, mintAmount: undefined }
    }

    const underlying0 = JSBI.BigInt(underlyingAmount0.toString())
    const underlying1 = JSBI.BigInt(underlyingAmount1.toString())
    const totalSupply = islandTotalSupply.quotient

    const amount0 = independentField === Field.CURRENCY_A ? independentAmount : dependentAmount
    const amount1 = independentField === Field.CURRENCY_A ? dependentAmount : independentAmount

    if (!amount0 || !amount1) {
      return { amount0: undefined, amount1: undefined, mintAmount: undefined }
    }

    let mintAmount: JSBI

    if (JSBI.equal(underlying0, JSBI.BigInt(0)) && JSBI.equal(underlying1, JSBI.BigInt(0))) {
      return { amount0: undefined, amount1: undefined, mintAmount: undefined }
    } else if (JSBI.equal(underlying0, JSBI.BigInt(0)) && JSBI.greaterThan(underlying1, JSBI.BigInt(0))) {
      mintAmount = JSBI.divide(JSBI.multiply(amount1.quotient, totalSupply), underlying1)
    } else if (JSBI.equal(underlying1, JSBI.BigInt(0)) && JSBI.greaterThan(underlying0, JSBI.BigInt(0))) {
      mintAmount = JSBI.divide(JSBI.multiply(amount0.quotient, totalSupply), underlying0)
    } else if (JSBI.greaterThan(underlying0, JSBI.BigInt(0)) && JSBI.greaterThan(underlying1, JSBI.BigInt(0))) {
      const amount0Mint = JSBI.divide(JSBI.multiply(amount0.quotient, totalSupply), underlying0)
      const amount1Mint = JSBI.divide(JSBI.multiply(amount1.quotient, totalSupply), underlying1)
      mintAmount = JSBI.lessThan(amount0Mint, amount1Mint) ? amount0Mint : amount1Mint
    } else {
      return { amount0: undefined, amount1: undefined, mintAmount: undefined }
    }

    return {
      amount0: amount0.quotient,
      amount1: amount1.quotient,
      mintAmount,
    }
  }, [
    independentAmount,
    dependentAmount,
    underlyingAmount0,
    underlyingAmount1,
    islandTotalSupply,
    independentField,
    contractMintAmounts,
  ])

  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(() => {
    if (singleSided) {
      return {
        [Field.CURRENCY_A]: mintingBalances[Field.CURRENCY_A],
        [Field.CURRENCY_B]: mintingBalances[Field.CURRENCY_B],
      }
    }
    return {
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }
  }, [dependentAmount, independentAmount, independentField, singleSided, mintingBalances])

  const amount0Min = useMemo(() => {
    if (!mintAmounts.amount0 || !currencyA) return undefined
    const minAmount = JSBI.divide(JSBI.multiply(mintAmounts.amount0, JSBI.BigInt(99)), JSBI.BigInt(100))
    return CurrencyAmount.fromRawAmount(currencyA, minAmount)
  }, [mintAmounts.amount0, currencyA])

  const amount1Min = useMemo(() => {
    if (!mintAmounts.amount1 || !currencyB) return undefined
    const minAmount = JSBI.divide(JSBI.multiply(mintAmounts.amount1, JSBI.BigInt(99)), JSBI.BigInt(100))
    return CurrencyAmount.fromRawAmount(currencyB, minAmount)
  }, [mintAmounts.amount1, currencyB])

  const amountSharesMin = useMemo(() => {
    if (!mintAmounts.mintAmount || !islandToken) return undefined
    const minAmount = singleSided
      ? JSBI.divide(JSBI.multiply(mintAmounts.mintAmount, JSBI.BigInt(10000 - stakingSlippageBPS)), JSBI.BigInt(10000))
      : JSBI.divide(JSBI.multiply(mintAmounts.mintAmount, JSBI.BigInt(99)), JSBI.BigInt(100))
    return CurrencyAmount.fromRawAmount(islandToken, minAmount)
  }, [mintAmounts.mintAmount, islandToken, singleSided, stakingSlippageBPS])

  const amountShares = useMemo(() => {
    if (!mintAmounts.mintAmount || !islandToken) return undefined
    return CurrencyAmount.fromRawAmount(islandToken, mintAmounts.mintAmount)
  }, [mintAmounts.mintAmount, islandToken])

  const invertPrice = Boolean(baseToken && token0 && !baseToken.equals(token0))

  // always returns the price with 0 as base token
  const price: Price<Token, Token> | undefined = useMemo(() => {
    // get the amount of quote currency
    return pool && token0 ? pool.priceOf(token0) : undefined
  }, [invertPrice, token1, token0, pool, feeAmount])

  // check for invalid price input (converts to invalid ratio)
  const invalidPrice = useMemo(() => {
    const sqrtRatioX96 = price ? encodeSqrtRatioX96(price.numerator, price.denominator) : undefined
    return (
      price &&
      sqrtRatioX96 &&
      !(
        JSBI.greaterThanOrEqual(sqrtRatioX96, TickMath.MIN_SQRT_RATIO) &&
        JSBI.lessThan(sqrtRatioX96, TickMath.MAX_SQRT_RATIO)
      )
    )
  }, [price])

  // used for ratio calculation when pool not initialized
  const mockPool = useMemo(() => {
    if (tokenA && tokenB && feeAmount && price && !invalidPrice) {
      const currentTick = priceToClosestTick(price)
      const currentSqrt = TickMath.getSqrtRatioAtTick(currentTick)
      return new Pool(tokenA, tokenB, feeAmount, currentSqrt, JSBI.BigInt(0), currentTick, [])
    } else {
      return undefined
    }
  }, [feeAmount, invalidPrice, price, tokenA, tokenB])

  // if pool exists use it, if not use the mock pool
  const poolForPosition: Pool | undefined = pool ?? mockPool

  // lower and upper limits in the tick space for `feeAmount`
  const tickSpaceLimits: {
    [bound in Bound]: number | undefined
  } = useMemo(() => {
    const tickSpacing = TICK_SPACINGS[feeAmount as keyof typeof TICK_SPACINGS]
    return {
      [Bound.LOWER]: feeAmount && tickSpacing ? nearestUsableTick(TickMath.MIN_TICK, tickSpacing) / 10 : undefined,
      [Bound.UPPER]: feeAmount && tickSpacing ? nearestUsableTick(TickMath.MAX_TICK, tickSpacing) / 10 : undefined,
    }
  }, [feeAmount])

  // parse typed range values and determine closest ticks
  // lower should always be a smaller tick
  const ticks: {
    [key: string]: number | undefined
  } = useMemo(() => {
    return {
      [Bound.LOWER]: ticksData?.lowerTick ?? undefined,
      [Bound.UPPER]: ticksData?.upperTick ?? undefined,
    }
  }, [ticksData, ticksData?.lowerTick, ticksData?.upperTick])

  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks || {}

  // specifies whether the lower and upper ticks is at the exteme bounds
  const ticksAtLimit = useMemo(
    () => ({
      [Bound.LOWER]: !!feeAmount && tickLower === tickSpaceLimits.LOWER,
      [Bound.UPPER]: !!feeAmount && tickUpper === tickSpaceLimits.UPPER,
    }),
    [tickSpaceLimits, tickLower, tickUpper, feeAmount]
  )

  const invalidRange = false

  // always returns the price with 0 as base token
  const pricesAtTicks = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(token0, token1, ticks[Bound.LOWER]),
      [Bound.UPPER]: getTickToPrice(token0, token1, ticks[Bound.UPPER]),
    }
  }, [token0, token1, ticks])

  const outOfRange = false

  const deposit0Disabled = Boolean(
    typeof tickUpper === 'number' && poolForPosition && poolForPosition.tickCurrent >= tickUpper
  )
  const deposit1Disabled = Boolean(
    typeof tickLower === 'number' && poolForPosition && poolForPosition.tickCurrent <= tickLower
  )

  // sorted for token order
  const depositADisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled && poolForPosition && tokenA && poolForPosition.token0.equals(tokenA)) ||
        (deposit1Disabled && poolForPosition && tokenA && poolForPosition.token1.equals(tokenA))
    )
  const depositBDisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled && poolForPosition && tokenB && poolForPosition.token0.equals(tokenB)) ||
        (deposit1Disabled && poolForPosition && tokenB && poolForPosition.token1.equals(tokenB))
    )

  // create position entity based on users selection
  const position: Position | undefined = useMemo(() => {
    if (
      !poolForPosition ||
      !tokenA ||
      !tokenB ||
      typeof tickLower !== 'number' ||
      typeof tickUpper !== 'number' ||
      invalidRange
    ) {
      return undefined
    }

    // mark as 0 if disabled because out of range
    const amount0 = !deposit0Disabled
      ? parsedAmounts?.[tokenA.equals(poolForPosition.token0) ? Field.CURRENCY_A : Field.CURRENCY_B]?.quotient
      : BIG_INT_ZERO
    const amount1 = !deposit1Disabled
      ? parsedAmounts?.[tokenA.equals(poolForPosition.token0) ? Field.CURRENCY_B : Field.CURRENCY_A]?.quotient
      : BIG_INT_ZERO

    if (amount0 !== undefined && amount1 !== undefined) {
      return Position.fromAmounts({
        pool: poolForPosition,
        tickLower,
        tickUpper,
        amount0,
        amount1,
        useFullPrecision: true, // we want full precision for the theoretical position
      })
    } else {
      return undefined
    }
  }, [
    parsedAmounts,
    poolForPosition,
    tokenA,
    tokenB,
    deposit0Disabled,
    deposit1Disabled,
    invalidRange,
    tickLower,
    tickUpper,
  ])

  // Extract amounts from parsedAmounts
  const { [Field.CURRENCY_A]: amount0Max, [Field.CURRENCY_B]: amount1Max } = parsedAmounts

  let error: ReactNode | undefined
  if (!account) {
    error = <>Connect Wallet</>
  }

  if (poolState === PoolState.INVALID) {
    error = error ?? <>Invalid pair</>
  }

  if (!parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]) {
    error = error ?? <>Enter an amount</>
  }

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts

  if (currencyAAmount && mintingBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount)) {
    error = <>Insufficient {currencies[Field.CURRENCY_A]?.symbol} balance</>
  }

  if (currencyBAmount && mintingBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount)) {
    error = <>Insufficient {currencies[Field.CURRENCY_B]?.symbol} balance</>
  }

  if (poolState === PoolState.INVALID) {
    error = error ?? <>Invalid pair</>
  }

  if (invalidPrice) {
    error = error ?? <>Invalid price input</>
  }

  if (
    (!parsedAmounts[Field.CURRENCY_A] && !depositADisabled) ||
    (!parsedAmounts[Field.CURRENCY_B] && !depositBDisabled)
  ) {
    error = error ?? <>Enter an amount</>
  }

  const invalidPool = poolState === PoolState.INVALID

  return {
    dependentField,
    currencies,
    island,
    pool,
    poolState,
    position,
    ticks,
    price,
    pricesAtTicks,
    noLiquidity,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
    currencyBalances: mintingBalances,
    parsedAmounts,
    error,
    amount0Max,
    amount1Max,
    amount0Min,
    amount1Min,
    amountSharesMin,
    amountShares,
    singleSided,
    dependentAmountFetching: underlyingBalancesFetching || contractLoading,

    // new
    singleSidedParsedAmount: debouncedSingleSidedAmount,
    singleSidedStakingSlippageBPS: stakingSlippageBPS,
    singleSidedCurrencyUserBalance,
  }
}

export function useSingleSidedKXRouterMintInfo(
  island: SubgraphIsland | null,
  useAutoCompound: boolean,
  baultAddress?: string,
  poolType?: 'Island' | 'Charm' | 'V2',
  poolAddress?: string
) {
  const { account, chainId } = useConnection()
  const { singleSidedAmount, zapSwapSlippage, stakingSlippageBPS, singleSided } = useMintState()
  const singleSidedCurrency = useMintStateSingleSidedCurrency()

  const debouncedSingleSidedAmount = useDebounce(
    singleSidedAmount ? parseUnits(singleSidedAmount, singleSidedCurrency?.decimals ?? 18).toString() : '0',
    500
  )

  // Get user balance for single-sided currency
  const userTokenBalances = useCurrencyBalances(
    account ?? undefined,
    useMemo(() => {
      const tokens: Currency[] = []
      if (singleSidedCurrency) tokens.push(singleSidedCurrency)
      return tokens
    }, [singleSidedCurrency]),
    REFETCH_POLICY_FAST
  )

  const singleSidedCurrencyUserBalance = userTokenBalances[0]

  const singleSidedCurrencyAmount = useMemo(() => {
    if (singleSidedCurrency) {
      return CurrencyAmount.fromRawAmount(singleSidedCurrency, debouncedSingleSidedAmount)
    }
    return undefined
  }, [singleSidedCurrency, debouncedSingleSidedAmount])

  const isSingleSidedBalanceLow = useMemo(() => {
    if (!account) {
      return false
    }
    if (!singleSidedCurrencyUserBalance || !singleSidedCurrencyAmount) {
      return true
    }
    return singleSidedCurrencyUserBalance.lessThan(singleSidedCurrencyAmount)
  }, [singleSidedCurrencyUserBalance, singleSidedCurrencyAmount, account])

  // Get token price for threshold validation
  const tokenAddresses = useMemo(() => {
    if (!singleSidedCurrency) return []
    return [singleSidedCurrency.wrapped.address]
  }, [singleSidedCurrency])

  const { prices: tokenPrices } = useGetTokenPrices(tokenAddresses)
  const singleSidedCurrencyPrice = tokenPrices?.[0] || null

  // Calculate USD value for threshold validation
  const singleSidedUSDValue = useMemo(() => {
    if (!singleSidedCurrencyPrice || !singleSidedAmount) return 0
    const numAmount = parseFloat(singleSidedAmount)
    if (isNaN(numAmount)) return 0
    return singleSidedCurrencyPrice * numAmount
  }, [singleSidedCurrencyPrice, singleSidedAmount])

  // Threshold validation
  const isBelowMinThreshold = useMemo(() => {
    return singleSidedUSDValue > 0 && singleSidedUSDValue < MIN_ZAP_USD_THRESHOLD
  }, [singleSidedUSDValue])

  const isAboveMaxThreshold = useMemo(() => {
    return singleSidedUSDValue > MAX_ZAP_USD_THRESHOLD
  }, [singleSidedUSDValue])

  const hasThresholdViolation = useMemo(() => {
    return isBelowMinThreshold || isAboveMaxThreshold
  }, [isBelowMinThreshold, isAboveMaxThreshold])

  // Determine output token based on pool type and auto-compound setting
  const outputTokenAddress = useMemo(() => {
    // For auto-compound with bault, always use bault address
    if (useAutoCompound && baultAddress) {
      return baultAddress
    }

    // Otherwise, use the pool address (island token, charm vault token, or LP token)
    if (poolType === 'Island') {
      return island?.id || poolAddress
    }

    // For Charm and V2, use the pool address
    return poolAddress
  }, [useAutoCompound, baultAddress, poolType, island?.id, poolAddress])

  const shouldFetchKXRouterQuote = useMemo(() => {
    return (
      !!singleSided &&
      !!singleSidedCurrencyAmount &&
      singleSidedCurrencyAmount.greaterThan(0) &&
      !hasThresholdViolation &&
      !!outputTokenAddress
    )
  }, [singleSided, singleSidedCurrencyAmount, hasThresholdViolation, outputTokenAddress])

  // Single unified KX Router hook
  const kxRouterResponse = useKXRouterZap(
    island, // Keep for backward compatibility
    singleSidedCurrency && 'address' in singleSidedCurrency ? singleSidedCurrency.address : 'native',
    debouncedSingleSidedAmount ? BigInt(debouncedSingleSidedAmount) : 0n,
    zapSwapSlippage,
    shouldFetchKXRouterQuote,
    outputTokenAddress
  )

  // Calculate output amounts and other derived values
  const outputAmount = useMemo(() => {
    if (!kxRouterResponse.data?.quote || !outputTokenAddress || !chainId) return undefined

    // Determine token symbol based on output type
    let symbol = 'LP'
    let name = 'LP Token'

    if (outputTokenAddress === baultAddress) {
      symbol = 'BAULT'
      name = 'Bault Token'
    } else if (poolType === 'Island') {
      symbol = 'ISLAND'
      name = 'Island Token'
    } else if (poolType === 'Charm') {
      symbol = 'CHARM-LP'
      name = 'Charm Vault Token'
    }

    const outputCurrency = new Token(chainId, outputTokenAddress, 18, symbol, name)
    return CurrencyAmount.fromRawAmount(outputCurrency, kxRouterResponse.data.quote)
  }, [kxRouterResponse.data, outputTokenAddress, chainId, baultAddress, poolType])

  const outputAmountMin = useMemo(() => {
    if (!outputAmount) return undefined
    return outputAmount.multiply(10000 - stakingSlippageBPS).divide(10000)
  }, [outputAmount, stakingSlippageBPS])

  // Get the best provider from the response
  const provider = kxRouterResponse.data?.provider || 'Unknown'
  const priceImpact = kxRouterResponse.data?.priceImpact || ''
  const sanitizedPriceImpact: Percent | null = sanitizePriceImpact(priceImpact)
  // Error handling
  let error: ReactNode | undefined
  if (isBelowMinThreshold) {
    error = <>Min deposit ${MIN_ZAP_USD_THRESHOLD.toFixed(2)}</>
  } else if (isAboveMaxThreshold) {
    error = <>Max deposit ${MAX_ZAP_USD_THRESHOLD.toFixed(2)}</>
  } else if (kxRouterResponse.isError) {
    error = <>Cannot zap, use double sided</>
  } else if (isSingleSidedBalanceLow && !kxRouterResponse.isLoading) {
    error = <>Insufficient {singleSidedCurrency?.symbol} balance</>
  } else if (kxRouterResponse.data?.quote && sanitizedPriceImpact === null) {
    error = <>Price impact unknown, use double sided</>
  } else if (
    kxRouterResponse.data?.quote &&
    sanitizedPriceImpact !== null &&
    sanitizedPriceImpact > MAX_ALLOWED_ZAP_PRICE_IMPACT
  ) {
    error = <>High impact, use double sided</>
  }

  return {
    isLoading: kxRouterResponse.isLoading,
    isError: kxRouterResponse.isError || hasThresholdViolation,
    outputAmount,
    outputAmountMin,
    provider,
    sanitizedPriceImpact,
    error,
    singleSidedCurrencyAmount,
    singleSidedStakingSlippageBPS: stakingSlippageBPS,
    singleSidedCurrencyUserBalance,
    kxRouterQuote: kxRouterResponse.data,
    // Threshold validation properties
    isBelowMinThreshold,
    isAboveMaxThreshold,
    hasThresholdViolation,
    singleSidedUSDValue,
    refetch: kxRouterResponse.refetch,
  }
}
